import dayjs from 'dayjs';
import { QrCodeVisitStatus } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo, useState } from 'react';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useQrCodeVisits from 'v2/hooks/useQrCodeVisits/useQrCodeVisits';
import PastVisitStatTiles from 'v2/pages/Visits/components/PastVisitStatTiles';
import VisitsPageContent from 'v2/pages/Visits/components/VisitsPageContent';
import VisitsTable from 'v2/pages/Visits/components/VisitsTable';
import {
  computePastVisitStats,
  filterPastVisitsByStat,
  PastVisitStatFilter
} from 'v2/pages/Visits/utils/pastVisitStats';
import { useVisitsSearch } from '../useVisitsSearch';

const Past: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (message: string) => showSnackbar(message, { severity: 'error' });

  const [selectedStat, setSelectedStat] = useState(PastVisitStatFilter.All);

  const { qrCodeVisits, loading } = useQrCodeVisits({ handleFetchError, translation });

  const visits = useMemo(() => {
    const now = dayjs().valueOf();
    return qrCodeVisits
      .filter((visit) => dayjs(visit.datetime).valueOf() < now && visit.status !== QrCodeVisitStatus.Cancelled)
      .sort((a, b) => dayjs(b.datetime).valueOf() - dayjs(a.datetime).valueOf());
  }, [qrCodeVisits]);

  const stats = useMemo(() => computePastVisitStats(visits), [visits]);

  const visitsFilteredByStat = useMemo(() => filterPastVisitsByStat(visits, selectedStat), [visits, selectedStat]);

  const { filterStr, setFilterStr, filteredVisits, sections, chips, countCaption } = useVisitsSearch({
    visits: visitsFilteredByStat
  });

  const handleStatClick = (stat: PastVisitStatFilter) => {
    setSelectedStat((currentStat) => (currentStat === stat ? PastVisitStatFilter.All : stat));
  };

  return (
    <VisitsPageContent
      breadcrumbTitle={translation.past}
      statTiles={
        <PastVisitStatTiles
          stats={stats}
          isLoading={loading}
          selectedStat={selectedStat}
          onStatClick={handleStatClick}
        />
      }
      filterStr={filterStr}
      onFilterStrChange={setFilterStr}
      sections={sections}
      chips={chips}
      countCaption={countCaption}
      loading={loading}
      hasData={visits.length > 0}
      hasResults={filteredVisits.length > 0}
      table={<VisitsTable visits={filteredVisits} isLoading={loading} showDetails />}
      noDataTitle={translation.no_visits_found}
      noDataContent={translation.no_past_visits_recorded}
    />
  );
};

export default Past;
